/**
 * CollapsibleSection — Section pliable utilisée dans les sidebars
 * (TrainingControls, AdminPage, etc.) pour offrir une UX
 * "accordéon" sur mobile compact tout en restant statique sur desktop.
 *
 * - En desktop / tablette (`hover: hover`) : section toujours ouverte
 *   (rendu équivalent à un `<section>`).
 * - En mobile (`hover: none, pointer: coarse`) : section collapsible avec
 *   un bouton header de 44px pour respecter les touch targets.
 *
 * Le composant délègue au DOM (`<details>` / `<summary>`) pour bénéficier
 * de l'accessibilité native (ARIA expanded, clavier, etc.).
 */

import { useId, type ReactNode } from 'react';
import { useResponsive } from '../hooks/useResponsive';

interface CollapsibleSectionProps {
  title: string;
  /** Si true, force l'état ouvert au montage (par défaut true). */
  defaultOpen?: boolean;
  /** Forcer collapsible même sur desktop (rare). */
  forceCollapsible?: boolean;
  /** Variante visuelle ("plain" pour conteneur sans bordure). */
  variant?: 'card' | 'plain';
  className?: string;
  children: ReactNode;
}

export default function CollapsibleSection({
  title,
  defaultOpen = true,
  forceCollapsible = false,
  variant = 'card',
  className = '',
  children,
}: CollapsibleSectionProps) {
  const { isCompact } = useResponsive();
  const sectionId = useId();

  const collapsible = forceCollapsible || isCompact;
  const wrapperClass = `collapsible-section collapsible-section-${variant} ${className}`;

  if (!collapsible) {
    return (
      <section className={wrapperClass}>
        <h4 className="collapsible-section-title">{title}</h4>
        <div className="collapsible-section-body">{children}</div>
      </section>
    );
  }

  return (
    <details
      className={`${wrapperClass} collapsible-section-collapsible`}
      open={defaultOpen}
    >
      <summary className="collapsible-section-summary" aria-controls={sectionId}>
        <span className="collapsible-section-title">{title}</span>
        <span className="collapsible-section-chevron" aria-hidden="true">›</span>
      </summary>
      <div id={sectionId} className="collapsible-section-body">
        {children}
      </div>
    </details>
  );
}
